import { useCallback } from "react";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import {
  hasFrontdeskBlockPermission,
  hasTenantPermission,
  isCustomFrontDeskRole,
  isTenantAdminRole,
} from "@/utils/accessControl";
import { getFrontdeskBlockForResource } from "@/constants/frontdeskPermissions";

export interface TenantPermissions {
  hasTenantPermissions: boolean;
  canRead: (resource: string) => boolean;
  canAdminister: (resource: string) => boolean;
}

/**
 * Returns tenant-scoped permission helpers for constrained tenant users.
 *
 * Tenant Admin roles use their granular resources. Frontdesk custom roles map
 * the same helper calls onto the five product blocks. Native system roles keep
 * their existing access and are not treated as constrained.
 */
export const useTenantPermissions = (): TenantPermissions => {
  const { userProfile } = useAuthenticatedUser();

  const isConstrained =
    (isTenantAdminRole(userProfile) || isCustomFrontDeskRole(userProfile)) &&
    userProfile?.effectiveRole?.type === "CUSTOM";

  const canRead = useCallback(
    (resource: string): boolean => {
      const frontdeskBlock = getFrontdeskBlockForResource(resource);
      if (isCustomFrontDeskRole(userProfile) && frontdeskBlock) {
        return hasFrontdeskBlockPermission(userProfile, frontdeskBlock, "read");
      }
      return (
        !isConstrained ||
        hasTenantPermission(userProfile, `${resource}:read`) ||
        hasTenantPermission(userProfile, `${resource}:administrate`)
      );
    },
    [userProfile, isConstrained],
  );

  const canAdminister = useCallback(
    (resource: string): boolean => {
      const frontdeskBlock = getFrontdeskBlockForResource(resource);
      if (isCustomFrontDeskRole(userProfile) && frontdeskBlock) {
        return hasFrontdeskBlockPermission(
          userProfile,
          frontdeskBlock,
          "administrate",
        );
      }
      return (
        !isConstrained ||
        hasTenantPermission(userProfile, `${resource}:administrate`)
      );
    },
    [userProfile, isConstrained],
  );

  return {
    hasTenantPermissions: isConstrained,
    canRead,
    canAdminister,
  };
};
